import type { QiValue } from './types';

/**
 * I-2 (Paul, 27 Oct: "a guard that blocks names or initials"): lodged QI comments must not
 * name a resident. Dotted initials block on their own (clinical abbreviations excepted);
 * a resident's first and last name together block where the population can be read (lock
 * and GPMS upload). Dates of birth, digit runs and other
 * signals stay POLICY review warnings (export/comments.ts, M3), so clinical abbreviations
 * such as RN or PRN and ordinary dates are never refused.
 */
// Dotted abbreviations that look like initials but are unlikely to be anyone's initials. Pairs
// that are plausible initials (S.C., G.P., R.N., A.M. ...) still block: a provider writes
// them undotted (GP, RN) instead.
const DOTTED_ABBREVIATIONS = new Set(['I.V.', 'P.R.N.', 'D.O.B.', 'T.D.S.', 'Q.I.D.', 'N.O.K.']);
// Dotted initials: "J.S.", "J. S", "A.B.C."
const DOTTED_INITIALS = /(?<!\p{L})\p{Lu}\.\s?\p{Lu}(?:\.\s?\p{Lu})*\.?(?!\p{L})/gu;

export function qiCommentHasStrongIdentifier(text: string): boolean {
  const normalised = text.normalize('NFKC');
  return [...normalised.matchAll(DOTTED_INITIALS)].some((m) => {
    const compact = m[0].replace(/\s+/g, '');
    return !DOTTED_ABBREVIATIONS.has(compact.endsWith('.') ? compact : `${compact}.`);
  });
}

export interface QiCommentPerson {
  names: string[];
  dateOfBirth?: string | null;
}

function tokens(text: string): string[] {
  return (
    text
      .normalize('NFKC')
      .toLocaleLowerCase('en-AU')
      .match(/[\p{L}\p{N}]+/gu) ?? []
  );
}

const partMatch = (name: string, words: string[]) =>
  tokens(name).some((part) => part.length >= 2 && words.includes(part));

/**
 * Names a resident: a first-name AND a last-name token of the same person both appear. This
 * blocks. A single matching word (a resident called May or Rose, or "Hill") stays a review
 * warning only, so ordinary words are never refused.
 */
export function qiCommentNamesPerson(text: string, people: readonly QiCommentPerson[]): boolean {
  const words = tokens(text);
  return people.some(
    (p) =>
      p.names.length >= 2 &&
      partMatch(p.names[0] ?? '', words) &&
      p.names.slice(1).some((last) => partMatch(last, words)),
  );
}

/** Any single resident name part of two or more letters. Review warning only. */
function qiCommentMentionsPersonPart(text: string, people: readonly QiCommentPerson[]): boolean {
  const words = tokens(text);
  return people.some((p) => p.names.some((name) => partMatch(name, words)));
}

/** A resident name or date of birth. Feeds the POLICY review warning only. */
export function qiCommentMatchesPerson(text: string, people: readonly QiCommentPerson[]): boolean {
  if (qiCommentMentionsPersonPart(text, people)) return true;
  const lower = text.normalize('NFKC').toLocaleLowerCase('en-AU');
  return people.some((p) => {
    if (!p.dateOfBirth) return false;
    const [year, month, day] = p.dateOfBirth.split('-');
    return (
      lower.includes(p.dateOfBirth) ||
      lower.includes(`${Number(day)}/${Number(month)}/${year}`) ||
      lower.includes(`${day}/${month}/${year}`)
    );
  });
}

/**
 * Manual Part A (September 2026) §§10.3, 11.3, 15.3: the weight-loss and ADL comments say WHY the
 * weights or previous assessment were not recorded ("Include comments as to why the weights were
 * not recorded"), and are required when that count is above zero.
 */
const DRAFTS: Readonly<Record<string, { trigger: string; noun: string }>> = {
  'UPWL-06': { trigger: 'UPWL-04', noun: 'required weight record(s)' },
  'UPWL-13': { trigger: 'UPWL-11', noun: 'required weight record(s)' },
  'ADL-07': { trigger: 'ADL-04', noun: 'previous ADL assessment(s)' },
};
const triggerCount = (
  code: string,
  values: Readonly<Record<string, QiValue | undefined>>,
): number | null => {
  const count = DRAFTS[code] && values[DRAFTS[code].trigger];
  return count?.state === 'measured' && typeof count.value === 'number' && count.value > 0
    ? count.value
    : null;
};

/**
 * Subject mode: the count and the distinct reasons recorded on the excluded residents' rows.
 * A reason carrying initials is left out; resident names are checked again at lock and export.
 * Without any recorded reason the draft points to the source evidence instead.
 */
export function qiDraftExclusionComment(
  code: string,
  values: Readonly<Record<string, QiValue | undefined>>,
  reasons: readonly (string | null | undefined)[] = [],
): string | null {
  const n = triggerCount(code, values);
  if (n === null) return null;
  const head = `${n} ${DRAFTS[code]!.noun} not recorded`;
  const seen = new Set<string>();
  const distinct = reasons
    .map((r) =>
      r
        ?.replace(/\s+/g, ' ')
        .trim()
        .replace(/[.;]+$/, ''),
    )
    .filter((r): r is string => !!r && !qiCommentHasStrongIdentifier(r))
    .filter((r) => {
      const key = r.toLocaleLowerCase('en-AU');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (!distinct.length) return `${head}; see source evidence.`;
  let text = `${head}: `;
  for (const [index, reason] of distinct.entries()) {
    const next = `${index ? '; ' : ''}${reason}`;
    if (text.length + next.length + 1 > 1000) break;
    text += next;
  }
  // A first reason too long to fit falls back to the evidence pointer, never a dangling colon.
  return text === `${head}: ` ? `${head}; see source evidence.` : `${text}.`;
}

/** Totals mode: an opening the provider completes with the reason (never a finished comment). */
export function qiExclusionCommentPrompt(
  code: string,
  values: Readonly<Record<string, QiValue | undefined>>,
): string | null {
  const n = triggerCount(code, values);
  return n === null ? null : `${n} ${DRAFTS[code]!.noun} were not recorded because `;
}
